import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';
import Toast from '../components/Toast';

/**
 * AdminSettings — /admin/settings
 * Section D of Phase 5. Global platform dials:
 *  Access:   maintenance_mode, registration_enabled
 *  Wallet:   deposit_enabled, withdraw_enabled
 *  Fees:     platform_fee_pct, creator_royalty_pct, referral_commission_pct (total ≤ 20)
 *  Provider: nowpayments_mode (stub | live)  ← reflects current selection; env keys live in Emergent's safe
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [maxTotal, setMaxTotal] = useState(20);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState({});

  const [feesForm, setFeesForm] = useState({ platform_fee_pct: '', creator_royalty_pct: '', referral_commission_pct: '' });
  const [pricingForm, setPricingForm] = useState({}); // {days: {pts, label, description}}
  const [rewardsForm, setRewardsForm] = useState({ enabled: true, login: 0, post: 0, referral: 0, trade: 0 });
  const [mintFeeInput, setMintFeeInput] = useState(10);
  const [perkFeeInput, setPerkFeeInput] = useState(10);
  const [perkFreeLimitInput, setPerkFreeLimitInput] = useState(100);
  const [foundingCount, setFoundingCount] = useState(null);
  const [resetScope, setResetScope] = useState(null); // 'test' | 'all' | null (confirm dialog)

  const refresh = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get('/admin/settings'),
        api.get('/admin/settings/audit', { params: { limit: 25 } }),
      ]);
      setSettings(s.data);
      setMaxTotal(s.data.max_total_fee_pct || 20);
      setFeesForm({
        platform_fee_pct: String(s.data.platform_fee_pct),
        creator_royalty_pct: String(s.data.creator_royalty_pct),
        referral_commission_pct: String(s.data.referral_commission_pct),
      });
      // Featured pricing form — copy pts as string per days key
      const pricing = {};
      Object.entries(s.data.featured_pricing || {}).forEach(([k, t]) => {
        pricing[k] = { pts: String(t.pts), label: t.label || '', description: t.description || '', days: t.days };
      });
      setPricingForm(pricing);
      setRewardsForm({
        enabled: !!s.data.rewards_enabled,
        login: Number(s.data.reward_login_streak_pts || 0),
        post: Number(s.data.reward_post_pts || 0),
        referral: Number(s.data.reward_referral_pts || 0),
        trade: Number(s.data.reward_trade_pts || 0),
      });
      setMintFeeInput(Number(s.data.clan_mint_fee_pts || 0));
      setPerkFeeInput(Number(s.data.perk_creation_fee_pts || 0));
      setPerkFreeLimitInput(Number(s.data.perk_free_creator_limit || 0));
      // Pull live founding-creator counter from the admin perks summary endpoint
      try {
        const fc = await api.get('/admin/perks/settings');
        setFoundingCount(Number(fc.data?.founding_creators_claimed ?? 0));
      } catch { setFoundingCount(null); }
      setAudit(a.data.entries || []);
      setLoading(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load settings');
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const patchOne = async (field, value, label) => {
    setSaving((s) => ({ ...s, [field]: true }));
    try {
      await api.patch('/admin/settings', { [field]: value });
      setToast({ msg: `${label} updated ✓` });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || `${label} failed` });
    } finally {
      setSaving((s) => ({ ...s, [field]: false }));
    }
  };

  const resetFoundingSlots = async (scope) => {
    setSaving((s) => ({ ...s, [`reset_${scope}`]: true }));
    try {
      const r = await api.post('/admin/perks/reset-founding-slots', null, { params: { scope } });
      const n = r.data?.users_reset ?? 0;
      setToast({ msg: `Cleared founding flag on ${n} user${n === 1 ? '' : 's'} · ${r.data?.founding_creators_claimed}/${perkFreeLimitInput} now claimed` });
      setResetScope(null);
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Reset failed' });
    } finally {
      setSaving((s) => ({ ...s, [`reset_${scope}`]: false }));
    }
  };

  const patchFees = async () => {
    setSaving((s) => ({ ...s, fees: true }));
    try {
      const body = {
        platform_fee_pct: Number(feesForm.platform_fee_pct),
        creator_royalty_pct: Number(feesForm.creator_royalty_pct),
        referral_commission_pct: Number(feesForm.referral_commission_pct),
      };
      await api.patch('/admin/settings', body);
      setToast({ msg: 'Fees updated ✓ (applies to future trades only)' });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Fee update failed' });
    } finally {
      setSaving((s) => ({ ...s, fees: false }));
    }
  };

  const patchPricing = async () => {
    setSaving((s) => ({ ...s, pricing: true }));
    try {
      const out = {};
      Object.entries(pricingForm).forEach(([k, t]) => {
        out[k] = {
          days: Number(t.days),
          pts: Number(t.pts),
          label: t.label,
          description: t.description,
        };
      });
      await api.patch('/admin/settings', { featured_pricing: out });
      setToast({ msg: 'Featured pricing updated ✓' });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Pricing update failed' });
    } finally {
      setSaving((s) => ({ ...s, pricing: false }));
    }
  };

  const patchRewards = async () => {
    setSaving((s) => ({ ...s, rewards: true }));
    try {
      await api.patch('/admin/settings', {
        rewards_enabled: rewardsForm.enabled,
        reward_login_streak_pts: Number(rewardsForm.login),
        reward_post_pts: Number(rewardsForm.post),
        reward_referral_pts: Number(rewardsForm.referral),
        reward_trade_pts: Number(rewardsForm.trade),
      });
      setToast({ msg: 'Rewards updated ✓' });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Rewards update failed' });
    } finally {
      setSaving((s) => ({ ...s, rewards: false }));
    }
  };

  const feesSum = (
    (Number(feesForm.platform_fee_pct) || 0) +
    (Number(feesForm.creator_royalty_pct) || 0) +
    (Number(feesForm.referral_commission_pct) || 0)
  );
  const feesOverCap = feesSum > maxTotal + 0.001;

  if (loading) return <AdminLayout active="settings"><div className="admin-page"><div className="admin-loading">Loading…</div></div></AdminLayout>;
  if (err) return <AdminLayout active="settings"><div className="admin-page"><div className="admin-error">{err}</div></div></AdminLayout>;

  return (
    <AdminLayout active="settings">
      <div className="admin-page" data-testid="admin-settings-page">
        <header className="admin-page-header">
          <h1 className="admin-page-title">Platform Settings</h1>
          <p className="admin-page-sub">
            Global dials that affect every user. Fee changes apply to future trades only — the ledger is never rewritten.
          </p>
        </header>

        {/* ---------- ACCESS ---------- */}
        <SettingsSection title="Access" icon="🛡️">
          <ToggleRow
            testid="toggle-maintenance"
            label="Maintenance mode"
            body="When on, everyone except admins sees a friendly “We'll be right back” page. Use this before deploys or emergency changes."
            value={settings.maintenance_mode}
            busy={!!saving.maintenance_mode}
            onChange={(v) => patchOne('maintenance_mode', v, 'Maintenance mode')}
            danger
          />
          <ToggleRow
            testid="toggle-registration"
            label="Registration enabled"
            body="Turn OFF to close new signups temporarily. Existing users can still log in."
            value={settings.registration_enabled}
            busy={!!saving.registration_enabled}
            onChange={(v) => patchOne('registration_enabled', v, 'Registration')}
          />
          <ToggleRow
            testid="toggle-show-seed-creators"
            label="Show seed creators"
            body="Turn ON to intermix the 18 hard-coded seed creators (Maya Lee, Luna, June Reyes, Isha, Zoe, Ren Sethi + 12 fan personas) with real creators on Home / Discover / Launches. Useful pre-launch so the platform doesn't feel empty. Turn OFF once real creators fill the feed."
            value={settings.show_seed_creators}
            busy={!!saving.show_seed_creators}
            onChange={(v) => patchOne('show_seed_creators', v, 'Show seed creators')}
          />
        </SettingsSection>

        {/* ---------- WALLET ---------- */}
        <SettingsSection title="Wallet" icon="💰">
          <ToggleRow
            testid="toggle-deposit"
            label="Deposits enabled"
            body="Turn OFF to stop new crypto deposits (existing invoices continue to settle)."
            value={settings.deposit_enabled}
            busy={!!saving.deposit_enabled}
            onChange={(v) => patchOne('deposit_enabled', v, 'Deposits')}
          />
          <ToggleRow
            testid="toggle-withdraw"
            label="Withdrawals enabled"
            body="Turn OFF to stop new payout requests. Pending withdrawals in your queue are unaffected."
            value={settings.withdraw_enabled}
            busy={!!saving.withdraw_enabled}
            onChange={(v) => patchOne('withdraw_enabled', v, 'Withdrawals')}
          />
        </SettingsSection>

        {/* ---------- FEES ---------- */}
        <SettingsSection title="Royalties & Fees" icon="📊">
          <p className="settings-hint">
            Total of the three fees must be ≤ <b>{maxTotal}%</b>. Currently: <b className={feesOverCap ? 'settings-red' : ''}>{feesSum.toFixed(2)}%</b>
          </p>
          <div className="settings-fee-grid">
            <PctInput
              testid="fee-platform"
              label="Platform fee"
              hint="Your cut on every trade."
              value={feesForm.platform_fee_pct}
              onChange={(v) => setFeesForm((f) => ({ ...f, platform_fee_pct: v }))}
            />
            <PctInput
              testid="fee-creator"
              label="Creator royalty"
              hint="Paid to the creator whose Bit is traded."
              value={feesForm.creator_royalty_pct}
              onChange={(v) => setFeesForm((f) => ({ ...f, creator_royalty_pct: v }))}
            />
            <PctInput
              testid="fee-referral"
              label="Referral commission"
              hint="Paid to the referrer (if any)."
              value={feesForm.referral_commission_pct}
              onChange={(v) => setFeesForm((f) => ({ ...f, referral_commission_pct: v }))}
            />
          </div>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={patchFees}
            disabled={feesOverCap || saving.fees}
            data-testid="save-fees"
          >
            {saving.fees ? 'Saving…' : 'Save fee changes'}
          </button>
        </SettingsSection>

        {/* ---------- CLAN MINT FEE ---------- */}
        <SettingsSection title="Clan Mint Fee" icon="🏗️">
          <p className="settings-hint">
            Fixed Points fee every creator pays to mint their Clan. Credited to the platform. Applies to future mints only. Set to <b>0</b> to make Clan creation free.
          </p>
          <div className="settings-fee-grid">
            <div className="settings-pct-field">
              <label className="settings-pct-label">Mint fee</label>
              <div className="settings-pct-input-wrap">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100000"
                  value={mintFeeInput}
                  onChange={(e) => setMintFeeInput(Number(e.target.value) || 0)}
                  data-testid="mint-fee-input"
                  className="settings-pct-input"
                />
                <span className="settings-pct-suffix">pts</span>
              </div>
              <div className="settings-pct-hint">Charged once per Clan mint. Ledger reason: <code>clan_mint_fee</code>.</div>
            </div>
          </div>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={() => patchOne('clan_mint_fee_pts', mintFeeInput, 'Clan mint fee')}
            disabled={!!saving.clan_mint_fee_pts}
            data-testid="save-mint-fee"
          >
            {saving.clan_mint_fee_pts ? 'Saving…' : 'Save mint fee'}
          </button>
        </SettingsSection>

        {/* ---------- PERK CREATION FEE ---------- */}
        <SettingsSection title="Perk Creation Fee" icon="🎁">
          <p className="settings-hint">
            Points fee a creator pays each time they publish a new perk. The first <b>{perkFreeLimitInput}</b> creators to ever publish a perk are grandfathered as <b>Founding Perk Creators</b> and never pay this fee — no matter how many perks they publish afterwards. Set fee to <b>0</b> to disable the fee platform-wide.
            {foundingCount !== null && (
              <> <span style={{ opacity: 0.75 }}>· Currently <b>{foundingCount}</b>/{perkFreeLimitInput} founding slots claimed.</span></>
            )}
          </p>
          <div className="settings-fee-grid">
            <div className="settings-pct-field">
              <label className="settings-pct-label">Fee per perk</label>
              <div className="settings-pct-input-wrap">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100000"
                  value={perkFeeInput}
                  onChange={(e) => setPerkFeeInput(Number(e.target.value) || 0)}
                  data-testid="perk-fee-input"
                  className="settings-pct-input"
                />
                <span className="settings-pct-suffix">pts</span>
              </div>
              <div className="settings-pct-hint">Charged on every published perk. Ledger reason: <code>perk_creation_fee</code>.</div>
            </div>
            <div className="settings-pct-field">
              <label className="settings-pct-label">Free founding slots</label>
              <div className="settings-pct-input-wrap">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100000"
                  value={perkFreeLimitInput}
                  onChange={(e) => setPerkFreeLimitInput(Number(e.target.value) || 0)}
                  data-testid="perk-free-limit-input"
                  className="settings-pct-input"
                />
                <span className="settings-pct-suffix">creators</span>
              </div>
              <div className="settings-pct-hint">First N creators to publish are grandfathered — permanent free perks.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={() => patchOne('perk_creation_fee_pts', perkFeeInput, 'Perk fee')}
              disabled={!!saving.perk_creation_fee_pts}
              data-testid="save-perk-fee"
            >
              {saving.perk_creation_fee_pts ? 'Saving…' : 'Save perk fee'}
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={() => patchOne('perk_free_creator_limit', perkFreeLimitInput, 'Free founding slots')}
              disabled={!!saving.perk_free_creator_limit}
              data-testid="save-perk-free-limit"
            >
              {saving.perk_free_creator_limit ? 'Saving…' : 'Save free slots'}
            </button>
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => setResetScope('test')}
              disabled={!!saving.reset_test || foundingCount === 0}
              data-testid="reset-founding-slots-test"
              title="Unset founding_perk_creator only for is_test / is_demo / is_seed accounts."
            >
              {saving.reset_test ? 'Resetting…' : '↺ Reset test slots'}
            </button>
            <button
              type="button"
              className="admin-btn-danger"
              onClick={() => setResetScope('all')}
              disabled={!!saving.reset_all || foundingCount === 0}
              data-testid="reset-founding-slots-all"
              title="Nuclear pre-launch reset — clears the flag on every user."
            >
              {saving.reset_all ? 'Resetting…' : '⚠ Reset ALL slots'}
            </button>
          </div>
        </SettingsSection>

        {/* ---------- FEATURED PRICING ---------- */}
        <SettingsSection title="Featured Pricing" icon="🌟">
          <p className="settings-hint">
            Cost (in Points) creators pay to feature their Clan on Home. Applies to future purchases only.
          </p>
          <div className="settings-fee-grid">
            {Object.entries(pricingForm).sort(([a], [b]) => Number(a) - Number(b)).map(([k, t]) => (
              <div key={k} className="settings-pct-field">
                <label className="settings-pct-label">{t.label || `${t.days} days`}</label>
                <div className="settings-pct-input-wrap">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={t.pts}
                    onChange={(e) => setPricingForm((p) => ({ ...p, [k]: { ...p[k], pts: e.target.value } }))}
                    data-testid={`pricing-${k}-pts`}
                    className="settings-pct-input"
                  />
                  <span className="settings-pct-suffix">pts</span>
                </div>
                <div className="settings-pct-hint">{t.days}-day tier</div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={patchPricing}
            disabled={saving.pricing}
            data-testid="save-pricing"
          >
            {saving.pricing ? 'Saving…' : 'Save featured pricing'}
          </button>
        </SettingsSection>

        {/* ---------- REWARDS ENGINE ---------- */}
        <SettingsSection title="Rewards Engine" icon="🎁">
          <ToggleRow
            testid="toggle-rewards"
            label="Rewards enabled"
            body="Master switch. When off, no auto-credits fire even if amounts are set below."
            value={rewardsForm.enabled}
            busy={!!saving.rewards}
            onChange={(v) => setRewardsForm((r) => ({ ...r, enabled: v }))}
          />
          <div className="settings-fee-grid" style={{ marginTop: 12 }}>
            <RewardInput
              testid="reward-login"
              label="Login streak"
              hint="Credited once per day per user (20h cooldown)."
              value={rewardsForm.login}
              onChange={(v) => setRewardsForm((r) => ({ ...r, login: v }))}
            />
            <RewardInput
              testid="reward-post"
              label="Post created"
              hint="Credited once per Clan feed post."
              value={rewardsForm.post}
              onChange={(v) => setRewardsForm((r) => ({ ...r, post: v }))}
            />
            <RewardInput
              testid="reward-referral"
              label="Referral signup"
              hint="Credited to the referrer when a referred user joins."
              value={rewardsForm.referral}
              onChange={(v) => setRewardsForm((r) => ({ ...r, referral: v }))}
            />
            <RewardInput
              testid="reward-trade"
              label="Trade completed"
              hint="Credited to the trader on every buy AND sell."
              value={rewardsForm.trade}
              onChange={(v) => setRewardsForm((r) => ({ ...r, trade: v }))}
            />
          </div>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={patchRewards}
            disabled={saving.rewards}
            data-testid="save-rewards"
          >
            {saving.rewards ? 'Saving…' : 'Save reward amounts'}
          </button>
        </SettingsSection>

        {/* ---------- NOWPAYMENTS ---------- */}
        <SettingsSection title="Payment Provider" icon="🔐">
          <p className="settings-hint">
            Current mode: <b>{settings.nowpayments_mode.toUpperCase()}</b>. API keys live in Emergent's env safe;
            switch mode here without redeploying. Live mode only takes effect once real keys are set in the safe.
          </p>
          <div className="settings-mode-toggle">
            <button
              type="button"
              className={`admin-chip${settings.nowpayments_mode === 'stub' ? ' active' : ''}`}
              onClick={() => patchOne('nowpayments_mode', 'stub', 'NowPayments mode')}
              disabled={saving.nowpayments_mode}
              data-testid="np-mode-stub"
            >
              🧪 Stub (sandbox)
            </button>
            <button
              type="button"
              className={`admin-chip${settings.nowpayments_mode === 'live' ? ' active' : ''}`}
              onClick={() => patchOne('nowpayments_mode', 'live', 'NowPayments mode')}
              disabled={saving.nowpayments_mode}
              data-testid="np-mode-live"
            >
              🚀 Live (real payouts)
            </button>
          </div>
        </SettingsSection>

        {/* ---------- AUDIT LOG ---------- */}
        <SettingsSection title="Audit log" icon="🕰️">
          {audit.length === 0 ? (
            <div className="admin-empty" data-testid="audit-empty">No changes yet.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="audit-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Field</th>
                    <th>From</th>
                    <th>To</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e) => (
                    <tr key={e.id} data-testid={`audit-row-${e.id}`}>
                      <td className="admin-mono admin-small">{formatDate(e.at)}</td>
                      <td className="admin-mono admin-small">{e.field}</td>
                      <td>{formatVal(e.old)}</td>
                      <td>{formatVal(e.new)}</td>
                      <td className="admin-small">{e.admin_email || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SettingsSection>

        {/* ---------- DANGER ZONE ---------- */}
        <DangerZone />
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
      {resetScope && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="reset-founding-confirm"
          onClick={() => setResetScope(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface, #1a1a1a)', color: 'var(--text, #fff)',
              border: '1px solid var(--border, #333)', borderRadius: 12,
              padding: 24, maxWidth: 440, width: '100%',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>
              {resetScope === 'all' ? '⚠ Reset ALL founding slots?' : '↺ Reset test founding slots?'}
            </h3>
            <p style={{ fontSize: 14, opacity: 0.85, lineHeight: 1.5 }}>
              {resetScope === 'all' ? (
                <>
                  This will unset <code>founding_perk_creator</code> on <b>every user</b>,
                  freeing up all {perkFreeLimitInput} slots. Any real creators who claimed
                  a slot will lose it and have to re-claim on their next perk publish.
                  <br /><br />
                  Only use this <b>pre-launch</b> when you're 100% sure no real creators have claimed yet.
                </>
              ) : (
                <>
                  This will unset <code>founding_perk_creator</code> only on users flagged
                  <code> is_test</code>, <code>is_demo</code>, or <code>is_seed</code>.
                  Real creators keep their founding status intact.
                </>
              )}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => setResetScope(null)}
                data-testid="reset-founding-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className={resetScope === 'all' ? 'admin-btn-danger' : 'admin-btn-primary'}
                onClick={() => resetFoundingSlots(resetScope)}
                disabled={!!saving[`reset_${resetScope}`]}
                data-testid="reset-founding-confirm-btn"
              >
                {saving[`reset_${resetScope}`]
                  ? 'Resetting…'
                  : resetScope === 'all' ? 'Yes, reset ALL' : 'Yes, reset test slots'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function SettingsSection({ title, icon, children }) {
  return (
    <section className="settings-section">
      <h2 className="admin-section-title">
        <span style={{ marginRight: 6 }}>{icon}</span>
        {title}
      </h2>
      <div className="settings-card">{children}</div>
    </section>
  );
}

function DangerZone() {
  const [phrase, setPhrase] = useState('');
  const [alsoStats, setAlsoStats] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const EXPECTED = 'I UNDERSTAND — WIPE CLANSTER';

  const run = async () => {
    if (phrase !== EXPECTED) return;
    if (!window.confirm('Absolutely last chance. This deletes every non-admin account and all their data. Proceed?')) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const { data } = await api.post('/admin/reset/wipe', {
        confirmation: EXPECTED,
        also_reset_platform_stats: alsoStats,
      });
      setResult(data);
      setPhrase('');
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-section danger-zone" data-testid="danger-zone">
      <h2 className="admin-section-title" style={{ color: '#FF5722' }}>
        <span style={{ marginRight: 6 }}>☢️</span>
        Danger zone — Nuclear reset
      </h2>
      <div className="settings-card danger-card">
        <p className="danger-desc">
          Deletes every user except admins, plus every clan, trade, holding, badge,
          notification, chat message, seed account, and crypto record. Admin
          accounts are kept but their points_balance is reset to 0. This is
          irreversible — there is no undo. Use only before a fresh launch, never
          on a network with real users you care about.
        </p>
        <label className="danger-checkbox">
          <input
            type="checkbox"
            checked={alsoStats}
            onChange={(e) => setAlsoStats(e.target.checked)}
            data-testid="danger-reset-stats"
          />
          Also zero platform cash-flow counters (total deposits / withdrawals / fees)
        </label>
        <label className="danger-phrase-label">
          To confirm, type the exact phrase below:
          <code className="danger-phrase-code">{EXPECTED}</code>
        </label>
        <input
          type="text"
          className="danger-phrase-input"
          placeholder="Type the phrase exactly"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          data-testid="danger-phrase-input"
        />
        <button
          type="button"
          className="danger-wipe-btn"
          disabled={busy || phrase !== EXPECTED}
          onClick={run}
          data-testid="danger-wipe-btn"
        >
          {busy ? 'Wiping…' : 'Wipe everything'}
        </button>
        {err && <div className="admin-banner error" data-testid="danger-err">{err}</div>}
        {result && (
          <div className="admin-banner success" data-testid="danger-result">
            <div><b>Wipe complete</b> in {result.duration_ms} ms.</div>
            <div>
              Users deleted: <b>{result.users_deleted}</b> · Admins kept: <b>{result.admins_kept}</b>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8, fontFamily: 'JetBrains Mono, monospace' }}>
              {Object.entries(result.collections_wiped).map(([k, v]) => (
                <span key={k} style={{ marginRight: 12 }}>{k}: {v}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ToggleRow({ label, body, value, onChange, busy, danger, testid }) {
  return (
    <div className={`settings-row${danger && value ? ' danger-on' : ''}`}>
      <div className="settings-row-text">
        <div className="settings-row-label">{label}</div>
        <div className="settings-row-body">{body}</div>
      </div>
      <button
        type="button"
        className={`settings-switch${value ? ' on' : ''}`}
        onClick={() => onChange(!value)}
        disabled={busy}
        data-testid={testid}
        aria-pressed={value}
      >
        <span className="settings-switch-thumb" />
      </button>
    </div>
  );
}

function PctInput({ label, hint, value, onChange, testid }) {
  return (
    <div className="settings-pct-field">
      <label className="settings-pct-label">{label}</label>
      <div className="settings-pct-input-wrap">
        <input
          type="number"
          step="0.1"
          min="0"
          max="15"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="settings-pct-input"
        />
        <span className="settings-pct-suffix">%</span>
      </div>
      <div className="settings-pct-hint">{hint}</div>
    </div>
  );
}

function RewardInput({ label, hint, value, onChange, testid }) {
  return (
    <div className="settings-pct-field">
      <label className="settings-pct-label">{label}</label>
      <div className="settings-pct-input-wrap">
        <input
          type="number"
          step="1"
          min="0"
          max="1000"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          data-testid={testid}
          className="settings-pct-input"
        />
        <span className="settings-pct-suffix">pts</span>
      </div>
      <div className="settings-pct-hint">{hint}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
function formatVal(v) {
  if (v === true) return '✓ ON';
  if (v === false) return '× OFF';
  return String(v);
}
